import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { Data } from 'core/utilities';
import { useToast } from 'core/toast';
import { deleteDocument, getDocumentTypes, getUserUploadedDocuments, markReadyForReview, updateApplicationStatusToUnderwriting, uploadDocument } from 'core/api/underwriting';
import { CardBody, CardHeader, Button, ProgressBar } from 'view/components/common';
import { Spinner } from 'view/components/layout';
import IMAGES from 'assets/images';
import { Row, HiddenInput, DocumentImage, DocumentPreview, Asterix, DragAndDrop, DragAndDropWrapper,UploadDocumentText, MutedDragAndDropText, DocumentList, DocumentItem, DocumentItemContent, SpacedDocumentItemContent, DocumentItemName, ExpandButton, AddIcon, Paragraph, DocumentPageCardFooter, UploadCompleteWrapper, UploadComplete, TrashIcon, RequiredText } from './DocumentUpload.styles';
import { fileSizeExceedsLimit } from './documentUploadHelperFunctions';

interface DocumentUploadProps {
    stepNavigator : (increment: boolean) => () => void;
    complete      : Data;
    applicationId : string;
    applicantId   : string;
}

enum DocumentTypeTags
{
    EVERYONE = 'everyone',
    SELF_EMPLOYED = 'selfemployed',
    EMPLOYED = 'employed',
    HOMEOWNER = 'homeowner',
    INVESTMENT_PROPERTY_OWNER = 'investpropertyowner',
    DOWNPAYMENT_GIFT = 'downpaymentgift'
}

const handleUserUploadedDocuments = async (
    applicationId : string, 
    applicantId : string,
    types : Array<Data>,
    selfEmployed : boolean,
    ownerStatus : boolean,
    ownerInvestStatus : boolean,
    downpaymentGiftStatus : boolean,
    history : any,
    setDocuments : (data : Data) => void,
    setLoading : (loading : boolean) => void,
    setToastMessage : (message?: string | undefined) => void,

) => {
    const { success, reply : docReply } = await getUserUploadedDocuments(applicationId);

    if (success) {
        const { uploadedDocuments } = docReply as { uploadedDocuments : Array<Data> };

        const documentsUploadedByApplicant = uploadedDocuments.filter((doc : Data) => doc.applicant === applicantId);

        const uploadedIds = documentsUploadedByApplicant.reduce((m : Data, doc : Data) =>
            ({
                ...m,
                [doc._type] : doc.id
            }), {});
        const uploadedTypes = documentsUploadedByApplicant.map(doc => doc._type);

        setDocuments(types.reduce((docs : Data, documentType : Data) => {
            const { tags } = documentType;

            if (tags.includes(DocumentTypeTags.EVERYONE) ||
                tags.includes(DocumentTypeTags.SELF_EMPLOYED) && selfEmployed ||
                tags.includes(DocumentTypeTags.EMPLOYED) && !selfEmployed ||
                tags.includes(DocumentTypeTags.HOMEOWNER) && ownerStatus ||
                tags.includes(DocumentTypeTags.INVESTMENT_PROPERTY_OWNER) && ownerInvestStatus ||
                tags.includes(DocumentTypeTags.DOWNPAYMENT_GIFT) && downpaymentGiftStatus) {
                return {
                    ...docs,
                    [documentType.id] : {
                        id : uploadedIds[documentType.id],
                        uploaded : uploadedTypes.includes(documentType.id),
                        name     : documentType.name,
                        required : documentType.required
                    }
                };
            } else return docs;
        }, {}));

        setLoading(false);
    } else {
        setToastMessage('Failed to fetch uploaded documents, please try again later');
        history.push('/dashboard');
    }
};

const setIntervalForProgress = (setProgress: React.Dispatch<React.SetStateAction<number>>) => {
    return setInterval(() => {
        setProgress((oldProgress) => {
            if (oldProgress === 100) {
                return 0;
            }
            const diff = Math.random() * 10;
            return Math.min(oldProgress + diff, 90);
        });
    }, 100);
};

export const DocumentUpload: React.FunctionComponent<DocumentUploadProps> = ({ 
    stepNavigator, 
    complete, 
    applicationId, 
    applicantId 
}) => {
    const history = useHistory();
    const [ isUploadingOrDeleting, setIsUploadingOrDeleting ] = useState(false);
    const [ loading, setLoading ] = useState(true);
    const [ types, setTypes ] = useState<Array<Data>>([]);
    const [ documents, setDocuments ] = useState<Data>({});
    const { setToastMessage } = useToast();
    const fileInputRefs = useRef<any[]>([]);
    const [ selectedDocumentId, setSelectedDocumentId ] = useState<string>('');
    const dragAndDropRefs = useRef<any[]>([]);
    const [ dragOver, setDragOver ] = useState<boolean>(false);
    const [ progress, setProgress ] = useState<number>(0);

    const {
        employment,
        investments,
        user_address,
        downpayments
    } = complete;

    const ownerStatus = user_address.own;
    const ownerInvestStatus = investments && investments.length && investments[0].properties;
    const selfEmployed = employment && employment.length && employment[0].self_employed;
    const downpaymentGiftStatus = downpayments && downpayments.length && downpayments[0].gift;

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (isUploadingOrDeleting) {
            timer = setIntervalForProgress(setProgress);
        } else {
            setProgress(0);
        }

        return (() => {
            clearInterval(timer);
        });
    }, [ progress, isUploadingOrDeleting ]);

    useEffect(() => {
        (async () => {
            try {
                const { success, reply } = await getDocumentTypes();

                if (success) {
                    const { types } = reply as { types : Array<Data>; };

                    setTypes(types);

                    await handleUserUploadedDocuments(
                        applicationId, 
                        applicantId,
                        types,
                        selfEmployed,
                        ownerStatus,
                        ownerInvestStatus,
                        downpaymentGiftStatus,
                        history,
                        setDocuments,
                        setLoading,
                        setToastMessage
                    );
                } else {
                    setToastMessage();
                    history.push('/dashboard');
                }
            } catch (error) {
                setToastMessage();
            }
        })();
    }, [ setDocuments, setLoading ]);

    const onFileAdded = async (event : any) => {
        const file = event.target.files[0];

        if (fileSizeExceedsLimit(file)) return setToastMessage('File limit exceeded. File size should be less than 20 MB.');

        await handleFileUpload(file);

        event.target.files = null;
        event.target.value = '';
    };

    const handleFileUpload = async (file : any) => {
        const formData = new FormData();
        formData.append('document', file);
        formData.append('_type', selectedDocumentId);
        formData.append('applicant', applicantId); 
        setIsUploadingOrDeleting(true);

        try {
            const { success } = await uploadDocument(applicationId, formData);
            if (success) {
                await handleUserUploadedDocuments(
                    applicationId, 
                    applicantId,
                    types,
                    selfEmployed,
                    ownerStatus,
                    ownerInvestStatus,
                    downpaymentGiftStatus,
                    history,
                    setDocuments,
                    setLoading,
                    setToastMessage
                );

                setToastMessage('File successfully uploaded');
                setDocuments(documents => ({
                    ...documents,
                    [selectedDocumentId] :
                    {
                        ...documents[selectedDocumentId],
                        uploaded : true
                    }
                }));
            } else {
                setToastMessage('File upload failed, please try again');
            }

            setIsUploadingOrDeleting(false);
        } catch (error) {
            setToastMessage('File upload failed, please try again');
            setIsUploadingOrDeleting(false);
        }
    };

    const onDeleteDocument = async (documentType: string) => {
        setIsUploadingOrDeleting(true);

        try {
            const { id } = documents[documentType];
            const success = await deleteDocument(applicationId, id);

            if (success) {
                setToastMessage('File successfully deleted');
                setDocuments(documents => ({
                    ...documents,
                    [documentType] :
                    {
                        ...documents[documentType],
                        id : undefined,
                        uploaded : false
                    }
                }));
            } else {
                setToastMessage('File deletion failed, please try again');
            }
        } catch (error) {
            setToastMessage('File deletion failed, please try again');
        }

        setIsUploadingOrDeleting(false);
    };

    const documentItemOnClickHandler = (uploaded: boolean, isSelected: boolean, id: string) => {
        if (uploaded) return;
        setSelectedDocumentId(isSelected ? '' : id);
    };

    const userHasUploadedAllRequiredDocuments = Object.values(documents).reduce((success : boolean, document : Data) => {
        const { uploaded, required } = document;

        // Skip upload check for non required document types
        return required ? success && uploaded : success;
    }, true);

    const onSubmit = async () => {
        setLoading(true);

        const success = await markReadyForReview(applicationId, applicantId);

        const statusChangeSuccess = await updateApplicationStatusToUnderwriting(applicationId);

        if (success && statusChangeSuccess) {
            setTimeout(() => {
                stepNavigator(true)();
            }, 500);
        } else {
            setToastMessage('An error occurred when submitting your request, please try again');
            setLoading(false);
        }
    };

    const {
        uploaded : selectedDocumentIsUploaded
    } = documents[selectedDocumentId] ?? {
        uploaded : false
    };

    useEffect(() => {
        // @ts-ignore
        const handleDragEvent = (e) => {
            e.preventDefault();
        };

        window.addEventListener('dragover', handleDragEvent, false);
        window.addEventListener('drop', handleDragEvent, false);

        return () => {
            window.removeEventListener('drop', handleDragEvent);
            window.removeEventListener('dragover', handleDragEvent);
        };
    }, []);

    return (
        <>
            <CardHeader
                content={'Document Upload'}
                step={'Step 2'}
                headerCss='text-align: center;'
                backHandler={stepNavigator(false)}
            />
            <CardBody>
                <>
                    <Row>
                        <Paragraph>
                            Based off the information you provided, this is a list of documents we require to complete the underwriting for your application.
                        </Paragraph>
                    </Row>
                    <RequiredText>* Required Document</RequiredText>
                    {loading ? (
                        <Spinner css='height: inherit; margin-top: 125px;' />
                    ) : (
                        <>
                            <DocumentList>
                                {Object.entries(documents).map((document: [string, Data], index) => {
                                    const [ id, properties ] = document;
                                    const { uploaded, name, required } = properties as { uploaded: boolean; name: string; required: boolean };
                                    const isSelected = id === selectedDocumentId;

                                    return (
                                        <DocumentItem key={id}>
                                            <HiddenInput
                                                ref={(ref) => fileInputRefs.current[index] = ref}
                                                type="file"
                                                multiple
                                                onChange={onFileAdded}
                                            />
                                            <SpacedDocumentItemContent>
                                                <DocumentItemContent onClick={() => documentItemOnClickHandler(uploaded, isSelected, id)}>
                                                    <DocumentImage solid={isSelected || uploaded} src={IMAGES.document} height={40} width={40} />
                                                    <DocumentItemName>{required ? <><Asterix>*</Asterix>{name}</> : name}</DocumentItemName>
                                                </DocumentItemContent>
                                                {!uploaded ? (
                                                    <ExpandButton onClick={() => setSelectedDocumentId(isSelected ? '' : id)}>
                                                        <AddIcon rotated={isSelected}>+</AddIcon>
                                                    </ExpandButton>
                                                ) : (
                                                    <TrashIcon onClick={async () => {
                                                        if (!isUploadingOrDeleting) {
                                                            await onDeleteDocument(id);
                                                        }
                                                    }} src={IMAGES.icTrash} uploading={isUploadingOrDeleting} />
                                                )}
                                            </SpacedDocumentItemContent>
                                            {isSelected && 
                                                <DocumentPreview
                                                    ref={(ref) => dragAndDropRefs!.current[index] = ref}
                                                    selectView={!selectedDocumentIsUploaded}
                                                    onClick={() => {
                                                        if (!isUploadingOrDeleting && !selectedDocumentIsUploaded) {
                                                            fileInputRefs.current[index].click();
                                                        }
                                                    }}
                                                    onDragOver={() => setDragOver(true)}
                                                    onDragLeave={() => setDragOver(false)}
                                                    onDropCapture={async (e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                
                                                        if (!isUploadingOrDeleting) {
                                                            const file = e.dataTransfer.files[0];

                                                            if (fileSizeExceedsLimit(file)) return setToastMessage('File limit exceeded. File size should be less than 20 MB.');

                                                            try {
                                                                await handleFileUpload(file);
                                                            } catch (error) {
                                                                console.error(error);
                                                            }
                                                        }
                
                                                        setDragOver(false);
                                                    }}
                                                >
                                                    {selectedDocumentIsUploaded ? (
                                                        <UploadCompleteWrapper>
                                                            <UploadComplete src={IMAGES.uploadComplete} />
                                                            <UploadDocumentText>Upload complete!</UploadDocumentText>
                                                            <ProgressBar progress={100}></ProgressBar>
                                                        </UploadCompleteWrapper>
                                                    ) : (
                                                        <DragAndDropWrapper>
                                                            <DragAndDrop src={IMAGES.dragAndDrop} draggedOver={dragOver} />
                                                            <UploadDocumentText>Drop files here</UploadDocumentText>
                                                            <MutedDragAndDropText>Or click to add files...</MutedDragAndDropText>
                                                            {isUploadingOrDeleting && <ProgressBar progress={progress}></ProgressBar>}
                                                        </DragAndDropWrapper>
                                                    )}
                                                </DocumentPreview>}
                                        </DocumentItem>
                                    );
                                })}
                            </DocumentList>
                        </>
                    )}

                </>
            </CardBody>
            <DocumentPageCardFooter>
                <Button
                    title="Submit"
                    type="button"
                    disabled={!userHasUploadedAllRequiredDocuments || loading}
                    onClick={onSubmit}
                    css={'text-transform: none; padding-bottom: 3px;'}
                    arrowCss={'padding-left: 0px; margin-top: 5px;'}
                />
            </DocumentPageCardFooter>
        </>
    );
};
